"use client";

import { useEffect, useMemo, useState, useRef } from "react";

import { getSharedAudioContext } from "@/lib/audio/sharedAudioContext";

/**
 * MediaStream から音声レベルをリアルタイムに取得するフック。
 * 各バーの高さに対応する 0〜1 の正規化値の配列を返す。
 *
 * AudioContext は録音中の音量計測と共有するため、後片付けでは close せず自分が
 * 接続したノードを切り離すだけにする（Issue #1438）。
 *
 * intervalMs を渡すと、画面の描画ごとではなくその間隔で更新する。受験中は
 * アバターの動画・セルフプレビュー・録画のエンコードが同時に動くため、
 * 常時表示する音量計では更新の頻度を落として再描画の負荷を抑える（#1807）。
 */
export function useAudioAnalyser(
  stream: MediaStream | null,
  barCount: number,
  intervalMs?: number
): number[] {
  const [levels, setLevels] = useState<number[]>(() =>
    new Array(barCount).fill(0)
  );
  const rafIdRef = useRef<number>(0);
  const zeroLevels = useMemo(() => new Array(barCount).fill(0), [barCount]);

  useEffect(() => {
    if (!stream) return;

    let cancelled = false;
    let source: MediaStreamAudioSourceNode;

    const audioContext = getSharedAudioContext();
    if (!audioContext) return;

    try {
      source = audioContext.createMediaStreamSource(stream);
    } catch {
      return;
    }

    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 64;
    source.connect(analyser);

    const freqData = new Uint8Array(analyser.frequencyBinCount);

    function compute() {
      if (cancelled) return;

      analyser.getByteFrequencyData(freqData);

      const binCount = freqData.length;
      const binsPerBar = Math.max(1, Math.floor(binCount / barCount));
      const result = new Array(barCount);

      for (let i = 0; i < barCount; i++) {
        let sum = 0;
        const start = i * binsPerBar;
        const end = Math.min(start + binsPerBar, binCount);
        for (let j = start; j < end; j++) {
          sum += freqData[j];
        }
        result[i] = sum / ((end - start) * 255);
      }

      setLevels(result);
    }

    let timerId: ReturnType<typeof setInterval> | null = null;
    if (intervalMs && intervalMs > 0) {
      timerId = setInterval(compute, intervalMs);
    } else {
      const update = () => {
        compute();
        if (cancelled) return;
        rafIdRef.current = requestAnimationFrame(update);
      };
      rafIdRef.current = requestAnimationFrame(update);
    }

    return () => {
      cancelled = true;
      if (timerId) clearInterval(timerId);
      cancelAnimationFrame(rafIdRef.current);
      source.disconnect();
    };
  }, [stream, barCount, intervalMs]);

  return stream ? levels : zeroLevels;
}
